"use client";

import { useCallback, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import Link from "next/link";
import { Button } from "@/components/ui/button";

const secondaryLinkClasses =
  "inline-flex min-h-11 cursor-pointer touch-manipulation select-none items-center justify-center rounded-[0.625rem] border border-[var(--border)] bg-[var(--surface-elevated)] px-5 py-2.5 text-sm font-medium text-[var(--foreground)] shadow-[var(--shadow-inset-top)] transition-[color,background-color,border-color,box-shadow,transform] duration-150 hover:border-[var(--border-strong)] hover:bg-[var(--surface-3)] hover:brightness-[1.02] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[var(--bg)] active:scale-[0.985] active:brightness-[0.98]";

type AppErrorFallbackProps = {
  retry: () => void;
};

/**
 * How many soft retries before we reload the page outright.
 *
 * A soft retry re-runs the code already in memory. That is the right first move for a one-off
 * failure, and completely useless for the failure that actually put this screen in front of Tom on
 * 12 September 2026: a broken JavaScript bundle, where every retry re-ran the same broken bundle
 * and "Try again" looked like a dead button. A reload fetches the current deployment, so once a fix
 * has shipped the second click gets out of it.
 */
const SOFT_RETRIES_BEFORE_RELOAD = 1;
const ATTEMPTS_KEY = "ownly.error-retries";
/**
 * Retries stop counting together after this long. A soft retry that worked leaves its count behind,
 * and without this an unrelated error half an hour later would reload the page on the first click
 * instead of trying the cheap thing first. Two clicks a minute apart are the same incident; two an
 * hour apart are not.
 */
const ATTEMPT_WINDOW_MS = 60_000;

function readAttempts(): number {
  try {
    const raw = window.sessionStorage.getItem(ATTEMPTS_KEY);
    if (!raw) return 0;
    const [count, at] = raw.split(":").map(Number);
    if (!Number.isFinite(count) || !Number.isFinite(at)) return 0;
    return Date.now() - at > ATTEMPT_WINDOW_MS ? 0 : count;
  } catch {
    // Private browsing, or storage blocked. Fall back to always soft-retrying.
    return 0;
  }
}

function writeAttempts(value: number): void {
  try {
    window.sessionStorage.setItem(ATTEMPTS_KEY, `${value}:${Date.now()}`);
  } catch {
    // Nothing to do: the count is a convenience, not state anything depends on.
  }
}

export function AppErrorFallback({ retry }: AppErrorFallbackProps) {
  const router = useRouter();
  const checkedRef = useRef(false);

  useEffect(() => {
    if (checkedRef.current) return;
    checkedRef.current = true;

    // If the session expired while the app was open, the error boundary may have been
    // triggered by a failed server render or redirect that wasn't propagated cleanly.
    // Check the session here so we can redirect to /login silently rather than
    // leaving the user on the error screen.
    void fetch("/api/auth/check")
      .then((res) => {
        if (res.status === 401) {
          router.replace("/login");
        }
        // 200 or 503 (DB down): leave the error UI visible so the user can retry.
      })
      .catch(() => {
        // Network error: leave the error UI visible so the user can retry.
      });

    /**
     * Tell somebody, now.
     *
     * Sentry reports the error. This reports that a person is sitting in front of a screen that
     * will not load, which is the fact that decides whether anyone looks today. The pipeline was
     * broken for two days in September 2026 with Sentry working perfectly the whole time, because
     * its only alert was an email.
     *
     * Deliberately sends the path and nothing else: the endpoint composes the message itself and
     * refuses to repeat anything the browser gave it. Failure here is ignored, because an error
     * screen that also fails to report itself must still render.
     */
    void fetch("/api/error-screen-alert", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ pathname: window.location.pathname }),
      keepalive: true,
    }).catch(() => {});
  }, [router]);

  const handleRetry = useCallback(() => {
    const attempts = readAttempts();
    if (attempts >= SOFT_RETRIES_BEFORE_RELOAD) {
      writeAttempts(0);
      window.location.reload();
      return;
    }
    writeAttempts(attempts + 1);
    // Typed as required, but it comes from the framework and a missing one must not turn the
    // button into a crash on top of the crash it is already reporting.
    if (typeof retry === "function") retry();
    else window.location.reload();
  }, [retry]);

  return (
    <div className="flex min-h-[100dvh] w-full flex-col items-center justify-center gap-6 px-[var(--app-gutter-x)] py-12 text-center">
      <div className="w-full max-w-md rounded-[var(--radius-card)] border border-[var(--border)] bg-[var(--surface)] px-8 py-10 shadow-[var(--shadow-card)]">
        <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-[var(--label)]">
          {/* Clients hit this screen too (agreements, invoices, portal), so not the internal CRM name. */}
          Ownly Digital
        </p>
        <h1 className="mb-2 text-xl font-semibold text-[var(--foreground)]">Something went wrong</h1>
        <p className="mb-8 text-sm text-[var(--muted-foreground)]">
          We couldn&apos;t load this page. Please try again in a moment.
        </p>
        <div className="flex flex-col gap-3 sm:flex-row sm:justify-center">
          <Button type="button" variant="primary" className="w-full sm:w-auto" onClick={handleRetry}>
            Try again
          </Button>
          <Link href="/" className={`${secondaryLinkClasses} w-full sm:w-auto`}>
            Back to home
          </Link>
        </div>
      </div>
    </div>
  );
}
